<template>
  <div>
    <CButton color="link" class="px-0 mb-2" @click="backToAccount">
      <CIcon name="cil-arrow-left" /> {{ obj.bankId ? "Bank account" : "Bank accounts" }}
    </CButton>

    <!-- Page Header -->
    <CCard class="mb-4 border">
      <CCardBody class="p-4">
        <div class="d-flex flex-wrap justify-content-between align-items-center">
          <div class="mb-2 mb-md-0">
            <h1 class="h4 mb-1 font-weight-bold">{{ obj.name || "Bank statement" }}</h1>
            <p class="text-muted mb-0">
              {{ obj.date ? `Statement dated ${formatDate(obj.date)}` : "Statement document" }}
            </p>
          </div>
          <div v-if="obj.id" class="d-flex">
            <CButton color="light" class="mr-2" aria-label="Previous statement" :disabled="navigating" @click="previous">
              <CIcon name="cil-chevron-left" />
            </CButton>
            <CButton color="light" aria-label="Next statement" :disabled="navigating" @click="next">
              <CIcon name="cil-chevron-right" />
            </CButton>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <CCard class="border">
      <CCardBody>
        <div v-if="loading" class="text-center py-5">
          <CSpinner color="primary" />
          <p class="mt-3 mb-0 text-muted">Loading the statement…</p>
        </div>
        <div v-else-if="loadError" class="text-center py-5">
          <CIcon name="cil-warning" size="2xl" class="text-danger mb-3" />
          <h5>Couldn't load this statement</h5>
          <p class="text-muted mb-3">Check your connection and try again.</p>
          <CButton color="primary" @click="resetObj">
            <CIcon name="cil-reload" /> Try again
          </CButton>
        </div>
        <div v-else-if="!obj.documentId" class="text-center py-5">
          <CIcon name="cil-file" size="2xl" class="text-muted mb-3" />
          <h5>No document attached</h5>
          <p class="text-muted mb-0">Upload the statement file from the bank account page.</p>
        </div>
        <iframe
          v-else
          :src="pdfUrl"
          title="Bank statement document"
          width="100%"
          height="600px"
          class="border-0"
        ></iframe>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import BankStatementApi from "../../lib/bankStatementApi";
import { formatDate } from "@/utils/datetime";

// Read-only viewer for one uploaded bank statement. Statements are added, edited and deleted
// from the bank account page (Bank.vue).
export default {
  name: "BankStatement",
  data: () => {
    return {
      obj: {},
      loading: false,
      loadError: false,
      navigating: false,
      api: new BankStatementApi(),
    };
  },
  mounted() {
    this.resetObj();
  },
  watch: {
    "$route.params.id"() {
      this.resetObj();
    },
  },
  computed: {
    pdfUrl() {
      return apiUrl + "documents/file/" + this.obj.documentId;
    },
  },
  methods: {
    formatDate(value) {
      return formatDate(value);
    },
    resetObj() {
      if (!this.$route.params.id) {
        this.obj = {};
        return;
      }
      this.loading = true;
      this.loadError = false;
      this.api
        .get(this.$route.params.id)
        .then((response) => {
          this.obj = response.result || {};
        })
        .catch((error) => {
          console.error("Bank statement load failed", error);
          this.loadError = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    backToAccount() {
      this.$router.push({ path: this.obj.bankId ? `/tenants/Bank/${this.obj.bankId}` : "/tenants/BankList" });
    },
    goTo(request, direction) {
      this.navigating = true;
      request
        .then((response) => {
          const target = response.result;
          if (target && target.id && target.id !== this.obj.id) {
            this.$router.push({ path: `/tenants/BankStatement/${target.id}` });
          } else {
            this.$toast.info(`There's no ${direction} statement`);
          }
        })
        .catch((error) => {
          console.error("Bank statement navigation failed", error);
          this.$toast.error(`Couldn't open the ${direction} statement. Check your connection and try again.`);
        })
        .finally(() => {
          this.navigating = false;
        });
    },
    previous() {
      this.goTo(this.api.getPrev(this.obj.id), "previous");
    },
    next() {
      this.goTo(this.api.getNext(this.obj.id), "next");
    },
  },
};
</script>
